import type { ImagePickerAsset } from 'expo-image-picker';
import { Image } from 'expo-image';
import { VideoView, useVideoPlayer } from 'expo-video';
import { ArrowUp, ImagePlus, Search, Smile, Video, X } from 'lucide-react-native';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type RefObject,
} from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  type NativeSyntheticEvent,
  type TextInputContentSizeChangeEventData,
  type TextInputKeyPressEventData,
  type TextInputSelectionChangeEventData,
  View,
} from 'react-native';

import { IdentityHeader } from '@/src/components/IdentityHeader';
import type { GiphyGif } from '@/src/features/giphy/giphy-service';
import { COMMENT_EMOJIS } from '@/src/features/comments/comment-emojis';
import {
  imageAssetFromPasteEvent,
  preventDefaultPaste,
} from '@/src/features/media/clipboard-media';
import {
  ProfileMentionBadges,
  activeProfileMentionToken,
  focusTextInputAt,
  profileMentionLabel,
  visibleProfileMentions,
} from '@/src/features/posts/ProfileMentionInput';
import {
  getAssetAspectRatio,
  searchMentionProfiles,
  type CommentUploadMediaKind,
  type MentionCandidate,
  type ProfileMention,
} from '@/src/features/posts/post-service';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';

const COMPOSER_INPUT_MIN_HEIGHT = 44;
const COMPOSER_INPUT_MAX_HEIGHT = 148;

function inferredSelectionAfterTextChange({
  nextValue,
  previousSelection,
  previousValue,
}: {
  nextValue: string;
  previousSelection: { end: number; start: number };
  previousValue: string;
}) {
  if (previousSelection.start !== previousSelection.end) {
    return previousSelection;
  }

  const lengthDelta = nextValue.length - previousValue.length;

  if (lengthDelta === 0) {
    return previousSelection;
  }

  const cursor = Math.max(
    0,
    Math.min(nextValue.length, previousSelection.start + lengthDelta),
  );

  return { end: cursor, start: cursor };
}

export type SelectedCommentMedia = {
  asset: ImagePickerAsset;
  kind: CommentUploadMediaKind;
};

type MentionScope =
  | {
      kind: 'comment';
      postId: string;
    }
  | {
      kind: 'vehicle_comment';
      vehicleId: string;
    };

export type CommentComposerProps = {
  avatarInitial: string;
  avatarURL: string | null;
  body: string;
  canSubmit: boolean;
  compact?: boolean;
  currentUid: string | undefined;
  giphyGif: GiphyGif | null;
  inputRef: RefObject<TextInput | null>;
  loading: boolean;
  maxLength: number;
  mentionScope: MentionScope;
  onAttachPhoto: () => void;
  onAttachVideo: () => void;
  onChangeBody: (value: string) => void;
  onChangeFocus?: (focused: boolean) => void;
  onChangeMentions: (mentions: ProfileMention[]) => void;
  onClearMedia: () => void;
  onOpenGiphy: () => void;
  onPasteImageAsset?: (asset: ImagePickerAsset) => void;
  onSubmit: () => void;
  profileMentions: ProfileMention[];
  uploadMedia: SelectedCommentMedia | null;
  uploadStatus?: string | null;
};

export function CommentComposerCard({ children }: PropsWithChildren) {
  return <View style={styles.composerCard}>{children}</View>;
}

function SelectedVideoPreview({
  aspectRatio,
  uri,
}: {
  aspectRatio: number;
  uri: string;
}) {
  const player = useVideoPlayer(uri);
  const compactWidth = Math.min(160, Math.max(48, 84 * aspectRatio));

  return (
    <VideoView
      nativeControls
      contentFit="contain"
      player={player}
      style={[styles.selectedMedia, { aspectRatio, width: compactWidth }]}
    />
  );
}

function SelectedMediaPreview({ media }: { media: SelectedCommentMedia }) {
  if (media.kind === 'video') {
    return (
      <SelectedVideoPreview
        aspectRatio={getAssetAspectRatio(media.asset)}
        uri={media.asset.uri}
      />
    );
  }

  return (
    <Image
      accessibilityLabel="Selected comment image"
      cachePolicy="memory-disk"
      contentFit="cover"
      source={{ uri: media.asset.uri }}
      style={styles.selectedMedia}
    />
  );
}

export function CommentComposer({
  avatarInitial,
  avatarURL,
  body,
  canSubmit,
  compact = false,
  currentUid,
  giphyGif,
  inputRef,
  loading,
  maxLength,
  mentionScope,
  onAttachPhoto,
  onAttachVideo,
  onChangeBody,
  onChangeFocus,
  onChangeMentions,
  onClearMedia,
  onOpenGiphy,
  onPasteImageAsset,
  onSubmit,
  profileMentions,
  uploadMedia,
  uploadStatus,
}: CommentComposerProps) {
  const [inputHeight, setInputHeight] = useState(COMPOSER_INPUT_MIN_HEIGHT);
  const [selection, setSelection] = useState({
    end: body.length,
    start: body.length,
  });
  const [mentionCandidates, setMentionCandidates] = useState<
    MentionCandidate[]
  >([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const [emojiSearchText, setEmojiSearchText] = useState('');
  const emojiPickerRef = useRef<View | null>(null);
  const emojiToggleRef = useRef<View | null>(null);
  const mentionRequestRef = useRef(0);
  const postId =
    mentionScope.kind === 'comment' ? mentionScope.postId : undefined;
  const vehicleId =
    mentionScope.kind === 'vehicle_comment'
      ? mentionScope.vehicleId
      : undefined;
  const token = useMemo(
    () => activeProfileMentionToken(body, selection.start, profileMentions),
    [body, profileMentions, selection.start],
  );
  const tokenActive = Boolean(token);
  const activeMentions = visibleProfileMentions(profileMentions, body);
  const filteredEmojis = useMemo(() => {
    const query = emojiSearchText.trim().toLowerCase();

    if (!query) {
      return COMMENT_EMOJIS;
    }

    return COMMENT_EMOJIS.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.keywords.toLowerCase().includes(query) ||
        item.emoji === emojiSearchText.trim(),
    );
  }, [emojiSearchText]);
  const selectedMediaLabel = uploadMedia
    ? uploadMedia.kind === 'video'
      ? 'Video attached'
      : 'Photo attached'
    : giphyGif
      ? 'GIF attached'
      : null;
  const effectiveMaxLength = maxLength;

  useEffect(() => {
    if (selection.start <= body.length && selection.end <= body.length) {
      return;
    }

    setSelection({
      end: body.length,
      start: body.length,
    });
  }, [body.length, selection.end, selection.start]);

  useEffect(() => {
    const query = token?.query ?? '';

    if (!tokenActive) {
      mentionRequestRef.current += 1;
      setMentionCandidates([]);
      setMentionLoading(false);
      return;
    }

    setEmojiPickerVisible(false);
    setEmojiSearchText('');

    const requestId = mentionRequestRef.current + 1;
    mentionRequestRef.current = requestId;
    setMentionLoading(true);

    const timeout = setTimeout(() => {
      void searchMentionProfiles({
        postId,
        query,
        scope: mentionScope.kind,
        vehicleId,
      })
        .then((profiles) => {
          if (mentionRequestRef.current === requestId) {
            setMentionCandidates(profiles);
          }
        })
        .catch(() => {
          if (mentionRequestRef.current === requestId) {
            setMentionCandidates([]);
          }
        })
        .finally(() => {
          if (mentionRequestRef.current === requestId) {
            setMentionLoading(false);
          }
        });
    }, 180);

    return () => clearTimeout(timeout);
  }, [mentionScope.kind, postId, token?.query, tokenActive, vehicleId]);

  useEffect(() => {
    if (body.length === 0) {
      setInputHeight(COMPOSER_INPUT_MIN_HEIGHT);
    }
  }, [body.length]);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !emojiPickerVisible ||
      typeof document === 'undefined'
    ) {
      return;
    }

    function isInsideNode(ref: RefObject<View | null>, target: EventTarget | null) {
      const node = ref.current as unknown as {
        contains?: (target: EventTarget | null) => boolean;
      } | null;

      return Boolean(node?.contains?.(target));
    }

    function closeEmojiPickerOnOutsidePress(event: PointerEvent) {
      if (
        isInsideNode(emojiPickerRef, event.target) ||
        isInsideNode(emojiToggleRef, event.target)
      ) {
        return;
      }

      setEmojiPickerVisible(false);
      setEmojiSearchText('');
    }

    document.addEventListener('pointerdown', closeEmojiPickerOnOutsidePress);

    return () => {
      document.removeEventListener(
        'pointerdown',
        closeEmojiPickerOnOutsidePress,
      );
    };
  }, [emojiPickerVisible]);

  function handleKeyPress(
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) {
    if (Platform.OS !== 'web' || event.nativeEvent.key !== 'Enter') {
      return;
    }

    const nativeEvent = event.nativeEvent as TextInputKeyPressEventData & {
      shiftKey?: boolean;
    };

    if (nativeEvent.shiftKey) {
      return;
    }

    event.preventDefault();
    onSubmit();
  }

  function handleContentSizeChange(
    event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>,
  ) {
    const nextHeight = Math.min(
      COMPOSER_INPUT_MAX_HEIGHT,
      Math.max(
        COMPOSER_INPUT_MIN_HEIGHT,
        Math.ceil(event.nativeEvent.contentSize.height),
      ),
    );

    setInputHeight(nextHeight);
  }

  function handleBodyChange(nextBody: string) {
    setSelection((currentSelection) =>
      inferredSelectionAfterTextChange({
        nextValue: nextBody,
        previousSelection: currentSelection,
        previousValue: body,
      }),
    );
    onChangeBody(nextBody);
    onChangeMentions(visibleProfileMentions(profileMentions, nextBody));
  }

  async function handlePaste(event: unknown) {
    const asset = await imageAssetFromPasteEvent(event);

    if (!asset) {
      return;
    }

    preventDefaultPaste(event);
    onPasteImageAsset?.(asset);
  }

  function selectMention(candidate: MentionCandidate) {
    if (!token) {
      return;
    }

    const label = profileMentionLabel(candidate);
    const nextBody = `${body.slice(0, token.atOffset)}${label} ${body.slice(
      token.cursor,
    )}`;
    const existingMentions = profileMentions.filter(
      (mention) => mention.uid !== candidate.uid || mention.label !== label,
    );
    const nextCursor = token.atOffset + label.length + 1;
    const nextSelection = { end: nextCursor, start: nextCursor };

    onChangeBody(nextBody);
    onChangeMentions([...existingMentions, { uid: candidate.uid, label }]);
    setSelection(nextSelection);
    mentionRequestRef.current += 1;
    setMentionCandidates([]);
    setMentionLoading(false);
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 0);
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 80);
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 180);
  }

  function toggleEmojiPicker() {
    if (loading) {
      return;
    }

    setEmojiPickerVisible((current) => {
      const nextVisible = !current;

      if (nextVisible) {
        mentionRequestRef.current += 1;
        setMentionCandidates([]);
        setMentionLoading(false);
      } else {
        setEmojiSearchText('');
      }

      return nextVisible;
    });
  }

  function insertEmoji(emoji: string) {
    const selectionStart = Math.max(
      0,
      Math.min(body.length, selection.start),
    );
    const selectionEnd = Math.max(0, Math.min(body.length, selection.end));
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);
    const nextLength = body.length - (end - start) + emoji.length;

    if (nextLength > effectiveMaxLength) {
      return;
    }

    const nextBody = `${body.slice(0, start)}${emoji}${body.slice(end)}`;
    const nextCursor = start + emoji.length;
    const nextSelection = { end: nextCursor, start: nextCursor };

    mentionRequestRef.current += 1;
    setMentionCandidates([]);
    setMentionLoading(false);
    onChangeBody(nextBody);
    onChangeMentions(visibleProfileMentions(profileMentions, nextBody));
    setSelection(nextSelection);
    setEmojiPickerVisible(false);
    setEmojiSearchText('');
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 0);
  }

  return (
    <View
      style={[
        styles.composerShell,
        compact ? styles.composerShellCompact : null,
      ]}
    >
      {!emojiPickerVisible && mentionLoading ? (
        <View style={styles.mentionStatus}>
          <ActivityIndicator color={colors.primary} size="small" />
          <Text style={styles.mentionStatusText}>Finding people...</Text>
        </View>
      ) : null}
      {!emojiPickerVisible && mentionCandidates.length > 0 ? (
        <View style={styles.mentionMenu}>
          {mentionCandidates.map((candidate) => (
            <Pressable
              key={candidate.uid}
              accessibilityRole="button"
              onPress={() => selectMention(candidate)}
              style={({ pressed }) => [
                styles.mentionCandidate,
                pressed ? styles.mentionCandidatePressed : null,
              ]}
            >
              <IdentityHeader
                avatarSize={36}
                containerStyle={styles.mentionCandidateIdentity}
                density="compact"
                founderBadge={candidate.founderBadge}
                fullName={candidate.fullName}
                interactive={false}
                photoURL={candidate.photoURL}
                uid={candidate.uid}
                username={candidate.username}
                verifiedBadge={candidate.verifiedBadge === true}
              />
            </Pressable>
          ))}
        </View>
      ) : null}
      {emojiPickerVisible ? (
        <View ref={emojiPickerRef} style={styles.emojiPicker}>
          <View style={styles.emojiSearchRow}>
            <Search color={colors.mutedText} size={17} strokeWidth={2.4} />
            <TextInput
              accessibilityLabel="Search emoji"
              autoCapitalize="none"
              autoCorrect={false}
              cursorColor={colors.primary}
              maxLength={40}
              onChangeText={setEmojiSearchText}
              placeholder="Search emoji"
              placeholderTextColor={colors.mutedText}
              returnKeyType="search"
              selectionColor={colors.primary}
              style={[
                styles.emojiSearchInput,
                Platform.OS === 'web' ? styles.composerInputWeb : null,
              ]}
              value={emojiSearchText}
            />
            {emojiSearchText ? (
              <Pressable
                accessibilityLabel="Clear emoji search"
                accessibilityRole="button"
                onPress={() => setEmojiSearchText('')}
                style={styles.emojiSearchClear}
              >
                <X color={colors.mutedText} size={16} strokeWidth={2.4} />
              </Pressable>
            ) : null}
          </View>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            style={styles.emojiScroll}
            contentContainerStyle={styles.emojiGrid}
          >
            {filteredEmojis.length > 0 ? (
              filteredEmojis.map((item) => (
                <Pressable
                  key={`${item.emoji}-${item.name}`}
                  accessibilityLabel={`Insert ${item.name} emoji`}
                  accessibilityRole="button"
                  disabled={loading}
                  onPress={() => insertEmoji(item.emoji)}
                  style={({ pressed }) => [
                    styles.emojiButton,
                    pressed && !loading ? styles.emojiButtonPressed : null,
                  ]}
                >
                  <Text style={styles.emojiText}>{item.emoji}</Text>
                </Pressable>
              ))
            ) : (
              <Text style={styles.emojiEmptyText}>No emoji found.</Text>
            )}
          </ScrollView>
        </View>
      ) : null}
      <View
        style={[
          styles.composerFooterSurface,
          compact ? styles.composerFooterSurfaceCompact : null,
        ]}
      >
        {(uploadMedia || giphyGif) && (
          <View style={styles.selectedMediaRow}>
            <View style={styles.selectedMediaFrame}>
              {uploadMedia ? (
                <SelectedMediaPreview media={uploadMedia} />
              ) : giphyGif ? (
                <Image
                  accessibilityLabel="Selected GIF"
                  cachePolicy="memory-disk"
                  contentFit="contain"
                  source={{ uri: giphyGif.previewURL }}
                  style={[
                    styles.selectedMedia,
                    {
                      aspectRatio:
                        giphyGif.width > 0 && giphyGif.height > 0
                          ? giphyGif.width / giphyGif.height
                          : 1,
                    },
                  ]}
                />
              ) : null}
            </View>
            <View style={styles.selectedMediaText}>
              <Text style={styles.selectedMediaTitle}>
                {selectedMediaLabel}
              </Text>
              <Text style={styles.selectedMediaSubtitle} numberOfLines={1}>
                Ready to post with your comment
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Remove selected media"
              accessibilityRole="button"
              onPress={onClearMedia}
              style={styles.removeMediaButton}
            >
              <X color={colors.text} size={18} strokeWidth={2.4} />
            </Pressable>
          </View>
        )}
        {uploadStatus ? (
          <View style={styles.commentUploadStatus}>
            <ActivityIndicator color={colors.primary} size="small" />
            <Text style={styles.commentUploadStatusText}>{uploadStatus}</Text>
          </View>
        ) : null}
        <View
          style={[
            styles.composerRow,
            compact ? styles.composerRowCompact : null,
          ]}
        >
          {compact ? null : (
            <Pressable
              accessibilityLabel="View your profile"
              accessibilityRole="button"
              onPress={() => {
                openUserProfile({
                  currentUid,
                  targetUid: currentUid,
                });
              }}
              style={styles.composerAvatar}
            >
              {avatarURL ? (
                <Image
                  accessibilityLabel=""
                  cachePolicy="memory-disk"
                  contentFit="cover"
                  source={{ uri: avatarURL }}
                  style={styles.commentAvatarImage}
                />
              ) : (
                <Text style={styles.commentAvatarText}>{avatarInitial}</Text>
              )}
            </Pressable>
          )}
          <View style={styles.composerBody}>
            <View style={styles.composerInputPill}>
              <View style={styles.composerTextInputArea}>
                <TextInput
                  ref={inputRef}
                  accessibilityLabel="Comment"
                  cursorColor={colors.primary}
                  editable={!loading}
                  maxLength={effectiveMaxLength}
                  multiline
                  returnKeyType="send"
                  scrollEnabled={inputHeight >= COMPOSER_INPUT_MAX_HEIGHT}
                  submitBehavior={
                    Platform.OS === 'web' ? 'newline' : 'submit'
                  }
                  onBlur={() => onChangeFocus?.(false)}
                  onContentSizeChange={handleContentSizeChange}
                  onChangeText={handleBodyChange}
                  onFocus={() => onChangeFocus?.(true)}
                  onKeyPress={handleKeyPress}
                  onSelectionChange={(
                    event: NativeSyntheticEvent<TextInputSelectionChangeEventData>,
                  ) => {
                    const nextSelection = event.nativeEvent.selection;

                    if (
                      typeof nextSelection?.start === 'number' &&
                      typeof nextSelection?.end === 'number'
                    ) {
                      setSelection(nextSelection);
                    }
                  }}
                  onSubmitEditing={Platform.OS === 'web' ? undefined : onSubmit}
                  placeholder="Comment publicly..."
                  placeholderTextColor={colors.mutedText}
                  selectionColor={colors.primary}
                  style={[
                    styles.composerInput,
                    compact ? styles.composerInputCompact : null,
                    Platform.OS === 'web' ? styles.composerInputWeb : null,
                    { height: inputHeight },
                  ]}
                  value={body}
                  {...(Platform.OS === 'web' && onPasteImageAsset
                    ? ({ onPaste: handlePaste } as object)
                    : null)}
                />
                <ProfileMentionBadges
                  mentions={activeMentions}
                  style={styles.composerMentionBadges}
                />
                <Text style={styles.composerCharacterCounter}>
                  {body.length}/{effectiveMaxLength}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.composerInputFooter}>
          <View style={styles.composerInlineActions}>
            <Pressable
              ref={emojiToggleRef}
              accessibilityLabel="Choose emoji"
              accessibilityRole="button"
              accessibilityState={{ expanded: emojiPickerVisible }}
              disabled={loading}
              onPress={toggleEmojiPicker}
              style={({ pressed }) => [
                styles.composerIconButton,
                emojiPickerVisible ? styles.composerIconButtonActive : null,
                pressed && !loading ? styles.composerIconButtonPressed : null,
                loading ? styles.composerIconButtonDisabled : null,
              ]}
            >
              <Smile color={colors.secondary} size={19} strokeWidth={2.5} />
            </Pressable>
            <Pressable
              accessibilityLabel="Attach photo"
              accessibilityRole="button"
              disabled={loading}
              onPress={onAttachPhoto}
              style={({ pressed }) => [
                styles.composerIconButton,
                pressed && !loading ? styles.composerIconButtonPressed : null,
                loading ? styles.composerIconButtonDisabled : null,
              ]}
            >
              <ImagePlus color={colors.secondary} size={19} strokeWidth={2.5} />
            </Pressable>
            <Pressable
              accessibilityLabel="Attach video"
              accessibilityRole="button"
              disabled={loading}
              onPress={onAttachVideo}
              style={({ pressed }) => [
                styles.composerIconButton,
                pressed && !loading ? styles.composerIconButtonPressed : null,
                loading ? styles.composerIconButtonDisabled : null,
              ]}
            >
              <Video color={colors.secondary} size={19} strokeWidth={2.5} />
            </Pressable>
            <Pressable
              accessibilityLabel="Choose GIF"
              accessibilityRole="button"
              disabled={loading}
              onPress={onOpenGiphy}
              style={({ pressed }) => [
                styles.composerIconButton,
                pressed && !loading ? styles.composerIconButtonPressed : null,
                loading ? styles.composerIconButtonDisabled : null,
              ]}
            >
              <Text style={styles.gifIconText}>GIF</Text>
            </Pressable>
          </View>
          <Pressable
            accessibilityLabel="Send comment"
            accessibilityRole="button"
            disabled={loading || !canSubmit}
            onPress={onSubmit}
            style={({ pressed }) => [
              styles.composerSendButton,
              pressed && !loading && canSubmit
                ? styles.composerSendButtonPressed
                : null,
              loading || !canSubmit ? styles.composerSendButtonDisabled : null,
            ]}
          >
            {loading ? (
              <ActivityIndicator color={colors.surface} size="small" />
            ) : (
              <ArrowUp color={colors.surface} size={20} strokeWidth={2.6} />
            )}
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  commentAvatarImage: {
    height: '100%',
    width: '100%',
  },
  commentAvatarText: {
    color: colors.secondary,
    fontSize: 15,
    fontWeight: '900',
  },
  commentUploadStatus: {
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: colors.accentSoft,
    borderColor: 'rgba(22, 143, 255, 0.2)',
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.xs,
    justifyContent: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  commentUploadStatusText: {
    color: colors.secondary,
    flexShrink: 1,
    fontSize: 12,
    fontWeight: '800',
  },
  composerAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderColor: colors.surface,
    borderRadius: 20,
    borderWidth: 2,
    flexShrink: 0,
    height: 40,
    justifyContent: 'center',
    marginTop: 2,
    overflow: 'hidden',
    width: 40,
  },
  composerBody: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  composerCard: {
    backgroundColor: colors.surface,
    borderColor: 'rgba(116, 67, 241, 0.16)',
    borderRadius: 18,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    padding: spacing.sm,
  },
  composerIconButton: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 999,
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  composerIconButtonActive: {
    backgroundColor: colors.accentSoft,
  },
  composerIconButtonDisabled: {
    opacity: 0.45,
  },
  composerIconButtonPressed: {
    backgroundColor: colors.surfacePressed,
    transform: [{ scale: 0.96 }],
  },
  composerCharacterCounter: {
    backgroundColor: colors.surface,
    borderRadius: 999,
    bottom: spacing.xs,
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    paddingHorizontal: spacing.xs,
    paddingVertical: 1,
    position: 'absolute',
    right: spacing.sm,
  },
  composerFooterSurface: {
    alignSelf: 'stretch',
    backgroundColor: colors.surfaceTint,
    borderColor: 'rgba(116, 67, 241, 0.12)',
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.xs,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.sm,
  },
  composerFooterSurfaceCompact: {
    borderRadius: 14,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
  },
  composerInlineActions: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: 'rgba(116, 67, 241, 0.16)',
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    flexShrink: 0,
    gap: spacing.xs,
    padding: 3,
  },
  composerInput: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: colors.text,
    flex: 1,
    flexShrink: 1,
    fontSize: 16,
    lineHeight: 22,
    maxHeight: COMPOSER_INPUT_MAX_HEIGHT,
    minHeight: COMPOSER_INPUT_MIN_HEIGHT,
    minWidth: 0,
    paddingBottom: spacing.lg,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    paddingTop: spacing.sm,
    textAlignVertical: 'top',
  },
  composerInputCompact: {
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
  },
  composerInputPill: {
    alignItems: 'stretch',
    backgroundColor: colors.surface,
    borderColor: 'rgba(116, 67, 241, 0.18)',
    borderRadius: 22,
    borderWidth: StyleSheet.hairlineWidth,
    minHeight: 46,
    overflow: 'hidden',
  },
  composerInputFooter: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 42,
  },
  composerInputWeb: {
    borderColor: 'transparent',
    outlineColor: 'transparent',
    outlineOffset: 0,
    outlineStyle: 'solid',
    outlineWidth: 0,
  },
  composerMentionBadges: {
    paddingBottom: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  composerRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.sm,
    width: '100%',
  },
  composerRowCompact: {
    gap: spacing.xs,
  },
  composerSendButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 999,
    flexShrink: 0,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  composerSendButtonDisabled: {
    backgroundColor: colors.mutedText,
    opacity: 0.5,
  },
  composerSendButtonPressed: {
    backgroundColor: colors.primaryPressed,
    transform: [{ scale: 0.96 }],
  },
  composerShell: {
    alignSelf: 'center',
    gap: spacing.xs,
    paddingHorizontal: 0,
    width: '100%',
  },
  composerShellCompact: {
    paddingHorizontal: 0,
  },
  composerTextInputArea: {
    minHeight: COMPOSER_INPUT_MIN_HEIGHT,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  emojiButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  emojiButtonPressed: {
    backgroundColor: colors.surfacePressed,
    transform: [{ scale: 0.96 }],
  },
  emojiEmptyText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
    paddingVertical: spacing.md,
    textAlign: 'center',
    width: '100%',
  },
  emojiGrid: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    justifyContent: 'center',
    paddingBottom: spacing.xs,
  },
  emojiPicker: {
    alignSelf: 'stretch',
    backgroundColor: colors.surface,
    borderColor: 'rgba(116, 67, 241, 0.16)',
    borderRadius: 18,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    padding: spacing.sm,
  },
  emojiScroll: {
    maxHeight: 194,
  },
  emojiSearchClear: {
    alignItems: 'center',
    borderRadius: 999,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  emojiSearchInput: {
    color: colors.text,
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    minHeight: 36,
    padding: 0,
  },
  emojiSearchRow: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: 'rgba(116, 67, 241, 0.16)',
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  emojiText: {
    fontSize: 22,
    lineHeight: 27,
  },
  gifIconText: {
    color: colors.secondary,
    fontSize: 11,
    fontWeight: '900',
    letterSpacing: 0,
  },
  mentionCandidate: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.sm,
  },
  mentionCandidateIdentity: {
    flex: 1,
  },
  mentionCandidatePressed: {
    backgroundColor: colors.surfacePressed,
  },
  mentionMenu: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  mentionStatus: {
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: colors.surfaceTint,
    borderColor: 'rgba(116, 67, 241, 0.14)',
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.xs,
    justifyContent: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  mentionStatusText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
  },
  removeMediaButton: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: 'rgba(26, 12, 62, 0.12)',
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  selectedMedia: {
    aspectRatio: 1,
    backgroundColor: colors.surfaceTint,
    borderRadius: 10,
    height: 54,
    width: 54,
  },
  selectedMediaFrame: {
    backgroundColor: colors.surface,
    borderColor: 'rgba(26, 12, 62, 0.08)',
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    justifyContent: 'center',
    minHeight: 58,
    overflow: 'hidden',
    padding: 2,
  },
  selectedMediaRow: {
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: colors.surfaceTint,
    borderColor: 'rgba(116, 67, 241, 0.14)',
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'flex-start',
    padding: spacing.xs,
  },
  selectedMediaSubtitle: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
  },
  selectedMediaText: {
    flex: 1,
    minWidth: 0,
  },
  selectedMediaTitle: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '900',
  },
});
